"use client";

import {
  ClerkProvider,
  RedirectToSignIn,
  SignedIn,
  SignedOut,
  useOrganization,
  useUser,
} from "@clerk/nextjs";
import { datadogRum } from "@datadog/browser-rum";
import { getUserRoles, isAsc } from "@procision-software/auth";
import {
  ConfirmProvider,
  DrawerProvider,
  ErrorScreen,
  TooltipProvider,
} from "@procision-software/ui";
import { useFlags, withLDProvider } from "launchdarkly-react-client-sdk";
import Image from "next/image";
import { usePathname, useRouter } from "next/navigation";
import { useMemo, type ReactElement, type ReactNode } from "react";
import { ToastContainer } from "react-toastify";
import "react-toastify/ReactToastify.css";
import tabs from "~/assets/images/tabs.svg?resource";
import {
  AppContextProvider,
  type AppContextProviderContextType,
} from "~/components/AppContextProvider";
import { RedirectToOrganizationChooser } from "~/components/auth/RedirectToOrganizationChooser";
import { RedirectToOrganizationalUnitChooser } from "~/components/auth/RedirectToOrganizationalUnitChooser";
import { WithOrganization } from "~/components/auth/WithOrganization";
import { WithOrganizationalUnit } from "~/components/auth/WithOrganizationalUnit";
import { WithoutOrganization } from "~/components/auth/WithoutOrganization";
import { WithoutOrganizationalUnit } from "~/components/auth/WithoutOrganizationalUnit";
import { AppLayout } from "~/components/layouts/AppLayout";
import { ToggleAdvancedMode } from "~/components/layouts/ToggleAdvancedMode";
import MarketingLayout from "~/components/layouts/marketing";
import { SignatureProvider } from "~/concept/signature/SignatureProvider";
import { env } from "~/env.mjs";
import { IntercomProviderWrapper } from "~/procision-core/providers/IntercomProviderWrapper";
import { MessagesProvider } from "~/procision-core/providers/MessagesProvider";
import { ThemeProvider } from "~/procision-core/providers/ThemeProvider";
import "~/styles/globals.css";
import { escapeValve } from "~/utils/redirect-escape-valve";
import { trpc } from "~/utils/trpc";

escapeValve(); // if we enter a redirect loop, this will scorched-earth the browser state in an attempt to break it

/*
 * despite being called a layout, this file is really application setup. It
 * does all the same work as the _app.tsx file in a Next.js app, but...
 * - it's a layout because appRouter requires it to be
 * - adds <html> and <body> tags because it's a new next requirement
 * - it incorporates a <AppLayout> component which would normally be invoked by wrapLayout() on a per-page basis
 * - it loses progressBar indicators because it doesn't work with current router
 * - <title> and friends are missing
 */

datadogRum.init({
  applicationId: env.NEXT_PUBLIC_DD_RUM_APPLICATION_ID,
  clientToken: env.NEXT_PUBLIC_DD_RUM_CLIENT_ID,
  site: "datadoghq.com",
  service: "webapp",
  env: env.NEXT_PUBLIC_INFRA_ENV,
  // Specify a version number to identify the deployed version of your application in Datadog
  // version: '1.0.0',
  sessionSampleRate: 100, // do capture console output & page meta
  sessionReplaySampleRate: 0, // don't capture what the user is doing
  trackUserInteractions: true, // clicks
  trackResources: true, // assets
  trackLongTasks: true, // performance
  defaultPrivacyLevel: "mask", // obscures all text: form field and HTML
  allowedTracingUrls: [
    (url) => {
      // expect url to be a string on the same domain with a path prefaced with /api/ . Return true if it is, false otherwise.
      return url.startsWith(`${window.location.origin}/api/`);
    },
  ],
});

/**
 * List pages you want to be publicly, or leave empty if every page requires authentication.
 * Use this naming strategy:
 *  "/"              for pages/index.js
 *  "/foo"           for pages/foo/index.js
 *  "/foo/bar"       for pages/foo/bar.js
 *  "/foo/[...bar]"  for pages/foo/[...bar].js
 */
const PUBLIC_ROUTES: string[] = [
  // "/forgot-password/[[...index]]",
  // "/reset-password/[[...index]]",
  // "/sign-in/[[...index]]",
  // "/sign-in/organization-chooser",
  // "/sign-in/organizational-unit-chooser",
  // "/sso-callback",
  // "/pat", // this conflicts with non-public route /patients
  // "/pat/[facilitySlug]",
  // "/pat/[facilitySlug]/[form]",
];

const AppContextWrapper = ({ children }: { children: ReactNode }) => {
  const router = useRouter();
  const { user: clerkUser } = useUser();
  const { organization: clerkOrganization } = useOrganization();
  const roles = getUserRoles(clerkUser, clerkOrganization);
  const perspective = roles.includes("platform:admin")
    ? "platform"
    : isAsc(roles)
      ? "asc"
      : "practice";

  const {
    data: contextData,
    refetch,
    ...contextTrpc
  } = trpc.appcontext.current.useQuery({}, { enabled: !!clerkUser && !!clerkOrganization });

  const appContext = useMemo(
    () =>
      contextData
        ? ({
            ...contextData,
            roles,
            perspective,
            refresh: () => void refetch(),
          } satisfies AppContextProviderContextType)
        : undefined,
    [contextData, roles, perspective, refetch]
  );

  // // TODO: Should we move redirect logic here to remove the need for the With(out) wrappers and simplify the tree?
  // if (!clerkUser) return <RedirectToSignIn />;
  // if (!clerkOrganization) return <RedirectToOrganizationChooser />;

  if (contextTrpc.isLoading) return null; // Intentionally not showing a loading screen here
  if (contextTrpc.isError) {
    return <ErrorScreen error={contextTrpc.error?.message} onBackClick={() => router.back()} />;
  }
  if (!contextData || !appContext) {
    return (
      <ErrorScreen
        error={"Sorry, there was an error retrieving your user."}
        onBackClick={() => router.back()}
      />
    );
  }
  if (clerkUser && !contextData.facility) {
    void router.push("/sign-in/organizational-unit-chooser");
    return null;
  }

  return <AppContextProvider context={appContext}>{children}</AppContextProvider>;
};

const MaintenancePage = () => (
  <div className="flex items-center justify-center">
    <div className="flex items-center gap-12">
      <Image src={tabs} width={415} height={495} alt="Page not found" />
      <div className="flex flex-col items-center justify-center gap-12">
        <h1>Maintenance Mode</h1>
        <p>Sorry, we are currently down for maintenance</p>
      </div>
    </div>
  </div>
);

export default withLDProvider({
  clientSideID: env.NEXT_PUBLIC_LAUNCHDARKLY_SDK_CLIENT_SIDE_ID,
})(
  trpc.withTRPC(function RootLayout({ children }: { children: ReactNode }): ReactElement {
    const pathname = usePathname() ?? "";
    const isPublicPage =
      PUBLIC_ROUTES.includes(pathname) || PUBLIC_ROUTES.some((route) => pathname.startsWith(route));
    const flags = useFlags<{ darkMode: boolean; maintenanceMode: boolean }>();
    const { darkMode, maintenanceMode } = flags;

    return (
      <html lang="en" suppressHydrationWarning>
        <body>
          <ThemeProvider forcedTheme={darkMode ? undefined : "light"} defaultTheme="light">
            <TooltipProvider>
              <ClerkProvider>
                {maintenanceMode && (
                  <MarketingLayout>
                    <MaintenancePage />
                  </MarketingLayout>
                )}
                {!maintenanceMode && (
                  <ConfirmProvider>
                    {isPublicPage ? (
                      children
                    ) : (
                      <>
                        <SignedIn>
                          <IntercomProviderWrapper>
                            <WithOrganization>
                              <WithOrganizationalUnit>
                                <AppContextWrapper>
                                  <SignatureProvider key={`signature-${pathname}`}>
                                    <DrawerProvider key={`drawer-${pathname}`}>
                                      <MessagesProvider>
                                        <AppLayout>
                                          {children}
                                          <ToggleAdvancedMode />
                                        </AppLayout>
                                      </MessagesProvider>
                                    </DrawerProvider>
                                  </SignatureProvider>
                                </AppContextWrapper>
                              </WithOrganizationalUnit>
                              <WithoutOrganizationalUnit>
                                <RedirectToOrganizationalUnitChooser />
                              </WithoutOrganizationalUnit>
                            </WithOrganization>
                            <WithoutOrganization>
                              <RedirectToOrganizationChooser />
                            </WithoutOrganization>
                          </IntercomProviderWrapper>
                        </SignedIn>
                        <SignedOut>
                          <DrawerProvider key={`pub-drawer-${pathname}`}>
                            {isPublicPage && children}
                            {!isPublicPage && <RedirectToSignIn />}
                          </DrawerProvider>
                        </SignedOut>
                      </>
                    )}
                    <ToastContainer />
                  </ConfirmProvider>
                )}
              </ClerkProvider>
            </TooltipProvider>
          </ThemeProvider>
        </body>
      </html>
    );
  })
);
